/** Commit size-driven layout outside ResizeObserver's delivery phase on legacy WebKit. */
export function createLayoutObserver(callback: ResizeObserverCallback, Native = ResizeObserver): ResizeObserver {
  if (!document.documentElement.classList.contains("compat-legacy-layout")) return new Native(callback);
  const pending = new Map<Element, ResizeObserverEntry>();
  let frame: number | undefined;
  const observer = new Native(entries => {
    entries.forEach(entry => pending.set(entry.target, entry));
    if (frame === undefined) frame = requestAnimationFrame(() => {
      frame = undefined;
      const entries = [...pending.values()].filter(entry => entry.target.isConnected); pending.clear();
      if (entries.length) callback(entries, compatible);
    });
  });
  const compatible: ResizeObserver = {
    observe: (node, options) => observer.observe(node, options),
    unobserve: node => { pending.delete(node); observer.unobserve(node); },
    disconnect: () => { observer.disconnect(); pending.clear(); if (frame !== undefined) cancelAnimationFrame(frame); frame = undefined; },
  };
  return compatible;
}

/** Ant Design uses the same native API. Keep native callbacks on modern browsers. */
export function installResizeCompatibility() {
  if (!document.documentElement.classList.contains("compat-legacy-layout")) return;
  const Native = window.ResizeObserver;
  window.ResizeObserver = class implements ResizeObserver {
    private readonly delegate: ResizeObserver;
    constructor(callback: ResizeObserverCallback) {
      this.delegate = createLayoutObserver(entries => callback(entries, this), Native);
    }
    observe(target: Element, options?: ResizeObserverOptions) { this.delegate.observe(target, options); }
    unobserve(target: Element) { this.delegate.unobserve(target); }
    disconnect() { this.delegate.disconnect(); }
  };
}
